import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import api from '../../services/api';
import { colors } from '../../Colors';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Fab,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  School as SchoolIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  PlayArrow as PlayIcon,
  Search as SearchIcon,
  People as PeopleIcon,
  Star as StarIcon,
  Book as BookIcon,
  Favorite as FavoriteIcon,
  AutoStories as AutoStoriesIcon,
  Spa as SpaIcon,
  Public as PublicIcon
} from '@mui/icons-material';

function SeanceProjectsScreen() {
  const navigate = useNavigate();
  const [seances, setSeances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [ageFilter, setAgeFilter] = useState('all');
  const [languageFilter, setLanguageFilter] = useState('all');

  useEffect(() => {
    const fetchSeances = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch seances from API
        const response = await api.seance.getAllSeances();
        
        // Extract seances from the API response
        const seancesData = response.data?.seances || [];
        
        // Transform the API data to match the expected structure
        const transformedSeances = seancesData.map(seance => ({
          id: seance.id,
          title: seance.title,
          theme: seance.theme,
          age: seance.age,
          language: seance.language,
          status: seance.status,
          created_at: seance.created_at,
          updated_at: seance.updated_at,
          created_by: seance.created_by,
          sequences: seance.sequences || [],
          thumbnail: seance.thumbnail || null, // Include thumbnail
          // Add config object for backward compatibility
          config: {
            subject: seance.theme || 'Unknown',
            sub_subject: '',
            age: seance.age || 'Unknown',
            level: seance.level || seance.config?.level || 'intermediate', // Get level from API or default to intermediate
            language: seance.language || 'fr',
            themes: [],
            number_of_sequences: seance.sequences?.length || 0
          },
          // Also store level at top level for easier access
          level: seance.level || seance.config?.level || 'intermediate',
          // Add generation_status for backward compatibility
          generation_status: {
            status: seance.status === 'completed' ? 'completed' : 
                   seance.status === 'draft' ? 'not_started' : 'in_progress',
            progress_percentage: seance.status === 'completed' ? 100 : 
                                seance.status === 'draft' ? 0 : 50
          }
        }));
        
        setSeances(transformedSeances);
      } catch (err) {
        console.error('Error fetching seances:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSeances();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'warning';
      case 'draft': return 'default';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon />;
      case 'in_progress': return <PlayIcon />;
      case 'failed': return <ErrorIcon />;
      default: return <SchoolIcon />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed': return 'Terminée';
      case 'in_progress': return 'En cours';
      case 'draft': return 'Brouillon';
      case 'failed': return 'Échec';
      default: return 'Inconnu';
    }
  };

  // Get unique ages and languages for filter options
  const uniqueAges = [...new Set(seances.map(s => s.age || s.config?.age).filter(Boolean))].sort();
  const uniqueLanguages = [...new Set(seances.map(s => s.language || s.config?.language).filter(Boolean))].sort();

  // Get language emoji and color
  const getLanguageIcon = (language) => {
    const lang = language || 'fr';
    switch (lang) {
      case 'fr':
      case 'French':
        return {
          emoji: '🗼', // Eiffel Tower
          bgColor: '#4A90E2' // French blue
        };
      case 'he':
      case 'Hebrew':
        return {
          emoji: '📖', // Book (perfect for Torah)
          bgColor: '#FFD93D' // Gold/yellow (Jewish tradition)
        };
      case 'it':
      case 'Italian':
        return {
          emoji: '🎭', // Theater masks (Italian culture)
          bgColor: '#6BCB77' // Italian green
        };
      case 'en':
      case 'English':
        return {
          emoji: '🌍', // Globe
          bgColor: '#FF6B6B' // Coral red
        };
      default:
        return {
          emoji: '📚', // Books
          bgColor: '#A8E6CF' // Light mint green
        };
    }
  };

  // Get level label in French
  const getLevelLabel = (level) => {
    const levelLower = (level || 'intermediate').toLowerCase();
    switch (levelLower) {
      case 'easy':
      case 'beginner':
        return 'Débutant';
      case 'intermediate':
        return 'Intermédiaire';
      case 'advanced':
        return 'Avancé';
      default:
        return 'Intermédiaire';
    }
  };

  // Filter seances based on search query, age, and language
  const filteredSeances = seances.filter(seance => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = (
        seance.title?.toLowerCase().includes(query) ||
        seance.theme?.toLowerCase().includes(query) ||
        seance.config?.subject?.toLowerCase().includes(query)
      );
      if (!matchesSearch) return false;
    }

    // Age filter
    if (ageFilter !== 'all') {
      const seanceAge = seance.age || seance.config?.age;
      if (seanceAge !== ageFilter) return false;
    }

    // Language filter
    if (languageFilter !== 'all') {
      const seanceLanguage = seance.language || seance.config?.language;
      if (seanceLanguage !== languageFilter) return false;
    }

    return true;
  });

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: colors.background.main }}>
        <Navigation />
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Card sx={{ borderRadius: 4, bgcolor: colors.background.secondary, boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={60} sx={{ mb: 2, color: colors.primary.main }} />
              <Typography variant="h4" sx={{ mb: 2, color: colors.text.primary, fontWeight: 'bold' }}>
                Chargement des séances...
              </Typography>
              <Typography variant="h6" sx={{ color: colors.text.secondary }}>
                Veuillez patienter pendant que nous récupérons vos séances.
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', background: colors.background.main }}>
        <Navigation />
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Card sx={{ borderRadius: 4, bgcolor: colors.background.secondary, boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Erreur lors du chargement des séances
                </Typography>
                <Typography variant="body1">
                  {error}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, color: colors.text.secondary }}>
                  Vérifiez que le serveur API est démarré
                </Typography>
              </Alert>
              <Button
                variant="contained"
                onClick={() => window.location.reload()}
                sx={{
                  bgcolor: colors.primary.main,
                  '&:hover': { bgcolor: colors.primary.dark }
                }}
              >
                Réessayer
              </Button>
            </CardContent>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: colors.background.main,
      overflowX: 'hidden',
      width: '100%'
    }}>
      <Navigation />
      <Container 
        maxWidth="xl" 
        sx={{ 
          py: 4,
          overflowX: 'hidden',
          width: '100%',
          px: { xs: 2, sm: 3, md: 4 },
          pb: 10 // Extra padding at bottom for FAB
        }}
      >
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h3" 
            component="h1" 
                sx={{
              fontWeight: 'bold', 
              color: colors.text.primary,
              mb: 1
            }}
          >
            Bibliothèque de Contenu
            </Typography>
          <Typography 
            variant="h6" 
                  sx={{
              color: colors.text.secondary,
              mb: 3
            }}
          >
            Découvrez du contenu Torah engageant pour tous les groupes d'âge
          </Typography>

          {/* Search and Filters */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              fullWidth
              placeholder="Rechercher du contenu, des thèmes ou des sujets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                flex: 1,
                minWidth: 300,
                bgcolor: colors.background.secondary,
                borderRadius: 2,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: colors.border.light,
                  },
                  '&:hover fieldset': {
                    borderColor: colors.border.medium,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: colors.primary.main,
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: colors.text.secondary }} />
                  </InputAdornment>
                ),
              }}
            />
            
            <FormControl 
              sx={{ 
                minWidth: 150,
                bgcolor: colors.background.secondary,
                borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.border.light,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.border.medium,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.primary.main,
                },
              }}
            >
              <InputLabel sx={{ color: colors.text.secondary }}>Tous les âges</InputLabel>
              <Select
                value={ageFilter}
                onChange={(e) => setAgeFilter(e.target.value)}
                label="Tous les âges"
                sx={{
                  color: colors.text.primary,
                  '& .MuiSvgIcon-root': {
                    color: colors.text.secondary,
                  },
                }}
              >
                <MenuItem value="all">Tous les âges</MenuItem>
                {uniqueAges.map((age) => (
                  <MenuItem key={age} value={age}>
                    {age}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl 
              sx={{ 
                minWidth: 150,
                bgcolor: colors.background.secondary,
                borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.border.light,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.border.medium,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.primary.main,
                },
              }}
            >
              <InputLabel sx={{ color: colors.text.secondary }}>Toutes les langues</InputLabel>
              <Select
                value={languageFilter}
                onChange={(e) => setLanguageFilter(e.target.value)}
                label="Toutes les langues"
                sx={{
                  color: colors.text.primary,
                  '& .MuiSvgIcon-root': {
                    color: colors.text.secondary,
                  },
                }}
              >
                <MenuItem value="all">Toutes les langues</MenuItem>
                {uniqueLanguages.map((lang) => (
                  <MenuItem key={lang} value={lang}>
                    {lang === 'fr' ? 'Français' : 
                     lang === 'he' ? 'עברית' : 
                     lang === 'en' ? 'Anglais' : 
                     lang === 'it' ? 'Italien' : 
                     lang.toUpperCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          </Box>

        {/* Seances Grid */}
          {filteredSeances.length > 0 ? (
          <Box 
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr', // 1 column on mobile
                sm: 'repeat(2, 1fr)', // 2 columns on tablet
                md: 'repeat(3, 1fr)', // 3 columns on desktop
                lg: 'repeat(3, 1fr)',
                xl: 'repeat(3, 1fr)'
              },
              gap: {
                xs: 2,
                sm: 2.5,
                md: 3
              },
              width: '100%',
              maxWidth: {
                xs: '100%',
                sm: '100%',
                md: '1400px', // Limit max width on larger screens
                lg: '1600px'
              },
              mx: 'auto', // Center the grid
              overflowX: 'hidden',
              boxSizing: 'border-box'
            }}
          >
            {filteredSeances.map((seance) => (
              <Box 
                key={seance.id}
                sx={{
                  display: 'flex',
                  minHeight: 0
                }}
              >
                <Card 
                  sx={{ 
                    borderRadius: 3, 
                    bgcolor: colors.background.secondary,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    transition: 'all 0.3s ease',
                    width: '100%',
                    maxWidth: {
                      xs: '100%',
                      sm: '100%',
                      md: '400px', // Limit card width on desktop
                      lg: '420px'
                    },
                        display: 'flex',
                        flexDirection: 'column',
                    height: {
                      xs: 300, // Slightly smaller on mobile
                      sm: 310,
                      md: 320
                    },
                    '&:hover': {
                      transform: 'translateY(-4px)',
                          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)'
                        }
                      }}
                    >
                  <CardContent 
                    sx={{ 
                      p: 2, 
                      flexGrow: 1, 
                      display: 'flex', 
                      flexDirection: 'column',
                      minHeight: 0,
                      height: '100%'
                    }}
                  >
                        {/* Content Type Badges */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mb: 1 }}>
                          <Chip 
                        label="Séance"
                            size="small"
                            sx={{ 
                          bgcolor: colors.status.warning,
                          color: 'white',
                          fontSize: '0.7rem',
                          height: 20,
                          fontWeight: 'bold'
                        }}
                      />
                      {seance.sequences?.length > 0 && (
                          <Chip 
                            label="Quiz" 
                            size="small"
                            sx={{ 
                            bgcolor: colors.secondary.main,
                            color: 'white',
                            fontSize: '0.7rem',
                            height: 20,
                            fontWeight: 'bold'
                          }}
                        />
                      )}
                        </Box>

                        {/* Thumbnail/Icon and Title */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                       {(() => {
                         // Show thumbnail if available, otherwise show language icon
                         if (seance.thumbnail) {
                           const thumbnailUrl = typeof seance.thumbnail === 'string' 
                             ? seance.thumbnail 
                             : (seance.thumbnail.img_url || seance.thumbnail.original_url || seance.thumbnail.url || seance.thumbnail.compressed_url);
                           
                           if (thumbnailUrl) {
                             const langIcon = getLanguageIcon(seance.language || seance.config?.language);
                             return (
                               <Box sx={{ position: 'relative', flexShrink: 0 }}>
                                 <Box
                                   component="img"
                                   src={thumbnailUrl}
                                   alt={seance.title}
                                   sx={{
                                     width: 80,
                                     height: 80,
                                     borderRadius: 2,
                                     objectFit: 'cover',
                                     border: '2px solid #ec4899',
                                     boxShadow: '0 2px 8px rgba(236, 72, 153, 0.2)',
                                     display: 'block'
                                   }}
                                 onError={(e) => {
                                   // If image fails to load, hide it and show fallback icon
                                   e.target.style.display = 'none';
                                   const fallback = e.target.parentElement?.querySelector('.thumbnail-fallback-icon');
                                   if (fallback) {
                                     fallback.style.display = 'flex';
                                   }
                                 }}
                                 />
                                 {/* Fallback language icon (hidden by default, shown if image fails) */}
                                 <Box
                                   sx={{
                                     width: 40, 
                                     height: 40,
                                     borderRadius: 2,
                                     bgcolor: langIcon.bgColor,
                                     display: 'none',
                                     alignItems: 'center',
                                     justifyContent: 'center',
                                     fontSize: '24px',
                                     position: 'absolute',
                                     top: 0,
                                     left: 0
                                   }}
                                   className="thumbnail-fallback-icon"
                                 >
                                   {langIcon.emoji}
                                 </Box>
                               </Box>
                             );
                           }
                         }
                         // Fallback to language icon
                         const langIcon = getLanguageIcon(seance.language || seance.config?.language);
                         return (
                           <Box
                             sx={{
                               width: 40, 
                               height: 40,
                               borderRadius: 2,
                               bgcolor: langIcon.bgColor,
                               display: 'flex',
                               alignItems: 'center',
                               justifyContent: 'center',
                               flexShrink: 0,
                               fontSize: '24px'
                             }}
                           >
                             {langIcon.emoji}
                           </Box>
                         );
                       })()}
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                        fontWeight: 'bold', 
                          color: colors.text.primary,
                          lineHeight: 1.3,
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          fontSize: '0.95rem'
                        }}
                      >
                        {seance.title}
                      </Typography>
                        </Box>
                      
                        {/* Tags */}
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                          {seance.theme && (
                        <Chip
                              label={seance.theme} 
                          size="small"
                              sx={{ 
                            bgcolor: '#FFD700',
                            color: colors.text.primary,
                                fontWeight: 'bold',
                            fontSize: '0.75rem',
                                height: 24
                              }}
                            />
                          )}
                      {seance.config?.subject && seance.config.subject !== seance.theme && (
                        <Chip
                          label={seance.config.subject}
                          size="small"
                              sx={{ 
                            bgcolor: '#FFD700',
                            color: colors.text.primary,
                                fontWeight: 'bold',
                            fontSize: '0.75rem',
                                height: 24
                              }}
                            />
                          )}
                    </Box>

                        {/* Description */}
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: colors.text.secondary,
                        mb: 1.5,
                        flexGrow: 1,
                        lineHeight: 1.5,
                        fontSize: '0.8rem'
                      }}
                    >
                      {seance.sequences?.length > 0 
                        ? `${seance.sequences.length} séquence${seance.sequences.length > 1 ? 's' : ''} éducative${seance.sequences.length > 1 ? 's' : ''}`
                        : 'Séance éducative interactive'
                      }
                        </Typography>

                        {/* Metadata */}
                        <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ScheduleIcon sx={{ fontSize: 16, color: colors.text.secondary }} />
                        <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                          {seance.sequences?.length || 0} séq.
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PeopleIcon sx={{ fontSize: 16, color: colors.text.secondary }} />
                        <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                          {seance.age || 'N/A'}
                          </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <StarIcon sx={{ fontSize: 16, color: colors.status.warning }} />
                        <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                          {seance.status === 'completed' ? '★4.8' : '★4.0'}
                          </Typography>
                        </Box>
                        </Box>

                        {/* Audience/Difficulty Tags */}
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                          {seance.age && (
                            <Chip 
                              label={`Ages ${seance.age}`} 
                              size="small"
                              sx={{ 
                            bgcolor: colors.neutral.gray,
                            color: colors.text.primary,
                                fontSize: '0.7rem',
                            height: 22
                              }}
                            />
                          )}
                          <Chip 
                            label={getLevelLabel(seance.level || seance.config?.level)}
                            size="small"
                            sx={{ 
                              bgcolor: colors.neutral.gray,
                              color: colors.text.primary,
                              fontSize: '0.7rem',
                              height: 22
                            }}
                          />
                            <Chip 
                         label={
                           seance.language === 'fr' ? 'Français' : 
                           seance.language === 'he' ? 'עברית' : 
                           seance.language === 'en' ? 'Anglais' : 
                           seance.language === 'it' ? 'Italien' : 
                           seance.language || 'FR'
                         }
                            size="small"
                            sx={{ 
                          bgcolor: colors.neutral.gray,
                          color: colors.text.primary,
                              fontSize: '0.7rem',
                          height: 22
                            }}
                          />
                      </Box>
                      
                        {/* Action Button */}
                      <Button
                        variant="contained"
                          fullWidth
                          startIcon={<BookIcon />}
                          onClick={() => navigate(`/seance/${seance.id}`)}
                        sx={{
                          bgcolor: colors.primary.main,
                          color: 'white',
                            borderRadius: 2,
                          py: 0.75,
                          fontSize: '0.85rem',
                          fontWeight: 'bold',
                            textTransform: 'none',
                          mt: 'auto',
                          '&:hover': {
                            bgcolor: colors.primary.dark,
                          }
                        }}
                      >
                        Voir le contenu
                      </Button>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        ) : (
          <Card sx={{ borderRadius: 4, bgcolor: colors.background.secondary, boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <SchoolIcon sx={{ fontSize: '4rem', color: colors.text.light, mb: 2 }} />
              <Typography variant="h4" sx={{ mb: 2, color: colors.text.primary, fontWeight: 'bold' }}>
                {searchQuery ? 'Aucun résultat trouvé' : 'Aucune séance créée'}
              </Typography>
              <Typography variant="h6" sx={{ color: colors.text.secondary, mb: 3 }}>
                {searchQuery 
                  ? 'Essayez de modifier vos critères de recherche'
                  : 'Commencez par créer votre première séance éducative'
                }
              </Typography>
              {!searchQuery && (
              <Button
                variant="contained"
                onClick={() => navigate('/seance/create')}
                startIcon={<AddIcon />}
                sx={{
                    bgcolor: colors.primary.main,
                    borderRadius: '20px',
                  px: 3,
                  py: 1,
                  fontSize: '1rem',
                  fontWeight: 'bold',
                    '&:hover': { bgcolor: colors.primary.dark }
                }}
              >
                Créer une séance
              </Button>
                )}
            </CardContent>
          </Card>
        )}

        {/* Floating Action Button */}
        <Fab
          color="primary"
          aria-label="add"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            bgcolor: colors.primary.main,
            '&:hover': {
              bgcolor: colors.primary.dark,
              transform: 'scale(1.1)'
            }
          }}
          onClick={() => navigate('/seance/create')}
        >
          <AddIcon />
        </Fab>
      </Container>
    </div>
  );
}

export default SeanceProjectsScreen;

